import type { AuthProvider } from '@refinedev/core'
import { signOut, onAuthStateChanged, setPersistence, browserLocalPersistence } from 'firebase/auth'

import { auth } from '../config/firebase'

import { API_CONFIG } from './constants.ts'

import {
  convertFirebaseUser,
  type AuthUser,
  type AuthResponse,
  type AuthCheckResponse,
} from '@/features/auth'

class FirebaseAuthProvider {
  private user: AuthUser | null = null
  private initialized = false
  private authStateListeners: ((user: AuthUser | null) => void)[] = []

  constructor() {
    // Set persistence to local storage (default but explicit)
    setPersistence(auth, browserLocalPersistence).catch(error => {
      console.error('Error setting persistence:', error)
    })

    // Listen for authentication state changes
    onAuthStateChanged(auth, user => {
      this.user = user ? convertFirebaseUser(user) : null
      this.initialized = true

      // Notify all listeners
      this.authStateListeners.forEach(listener => listener(this.user))
    })
  }

  private waitForInitialization(): Promise<void> {
    return new Promise(resolve => {
      if (this.initialized) {
        resolve()
        return
      }

      const listener = () => {
        resolve()
        const index = this.authStateListeners.indexOf(listener)
        if (index > -1) {
          this.authStateListeners.splice(index, 1)
        }
      }
      this.authStateListeners.push(listener)
    })
  }

  private async sendSignInLink(email: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_CONFIG.authApiUrl}/api/auth/send-login-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          (errorData.message as string) || `HTTP ${response.status}: Failed to send login link`
        )
      }

      // Store the email in localStorage to retrieve it later
      window.localStorage.setItem('emailForSignIn', email)

      return {
        success: true,
        successNotification: {
          message: 'Login link sent!',
          description: `We've sent a login link to ${email}. Please check your email and click the link to sign in.`,
        },
      }
    } catch (error) {
      console.error('Error sending email link:', error)
      const apiError = error as { message?: string }

      let errorMessage = 'Failed to send login link. Please try again.'
      if (
        apiError.message?.includes('invalid-email') ||
        apiError.message?.includes('Invalid email')
      ) {
        errorMessage = 'Please enter a valid email address.'
      } else if (
        apiError.message?.includes('too-many-requests') ||
        apiError.message?.includes('rate limit')
      ) {
        errorMessage = 'Too many requests. Please wait before trying again.'
      } else if (apiError.message?.includes('domain')) {
        errorMessage = 'Only @enter.de email addresses are allowed.'
      } else if (apiError.message) {
        errorMessage = apiError.message
      }

      return {
        success: false,
        error: new Error(errorMessage),
      }
    }
  }

  getAuthProvider(): AuthProvider {
    return {
      login: async ({ email, token }: { email?: string; token?: string }) => {
        try {
          // Handle email link sign in
          if (email) {
            return await this.sendSignInLink(email)
          }

          // Handle token-based login (from email link completion)
          if (token) {
            // Token is already validated by Firebase, just return success
            return {
              success: true,
              redirectTo: '/',
            }
          }

          return {
            success: false,
            error: new Error('Email is required for sign-in'),
          }
        } catch (error) {
          return {
            success: false,
            error: error as Error,
          }
        }
      },

      check: async (): Promise<AuthCheckResponse> => {
        // Skip authentication in development mode
        if (import.meta.env.DEV) {
          return {
            authenticated: true,
          }
        }

        await this.waitForInitialization()

        if (this.user) {
          return {
            authenticated: true,
          }
        }
        return {
          authenticated: false,
          redirectTo: '/login',
        }
      },

      logout: async (): Promise<AuthResponse> => {
        try {
          await signOut(auth)
          this.user = null
          return {
            success: true,
            redirectTo: '/login',
          }
        } catch (error) {
          return {
            success: false,
            error: error as Error,
          }
        }
      },

      onError: error => {
        if (error.status === 401) {
          return Promise.resolve({
            logout: true,
            redirectTo: '/login',
          })
        }
        return Promise.resolve({})
      },

      getIdentity: async () => {
        // Return mock user in development mode
        if (import.meta.env.DEV) {
          return {
            id: 'dev-user',
            name: 'Development User',
            email: 'dev@enter.de',
            avatar: undefined,
          }
        }

        await this.waitForInitialization()

        if (this.user) {
          return {
            id: this.user.uid,
            name: this.user.displayName || this.user.email || 'User',
            email: this.user.email,
            avatar: this.user.photoURL,
          }
        }
        return null
      },
    }
  }
}

export const firebaseAuthProvider = new FirebaseAuthProvider()
export const authProvider = firebaseAuthProvider.getAuthProvider()
